import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, LOGO_URL } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { CarCard } from "@/components/site/CarCard";
import { ArrowRight, Shield, Award, Sparkles, Bell } from "lucide-react";
import { toast } from "sonner";

export default function Home() {
  useSEO({
    title: "Πολυτελή Αυτοκίνητα προς Πώληση & Ενοικίαση",
    description: "Επιλεγμένη συλλογή από πολυτελή αυτοκίνητα. Mercedes-Benz, BMW, Porsche, Range Rover στη Vanguard Lux Cars.",
    path: "/",
  });
  const [featured, setFeatured] = useState([]);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    api.get("/cars/featured").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Παρακαλώ εισάγετε έγκυρο email.");
      return;
    }
    setSubscribing(true);
    try {
      const { data } = await api.post("/subscribers", { email });
      if (data.already_subscribed) {
        toast.success("Είστε ήδη στη λίστα. Θα επικοινωνήσουμε.");
      } else {
        toast.success("Είστε μέσα. Περιμένετε email για νέες παραλαβές.");
      }
      setEmail("");
    } catch {
      toast.error("Η εγγραφή απέτυχε. Δοκιμάστε ξανά.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-screen min-h-[700px] flex items-end overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1567808291548-fc3ee04dbcf0?w=2000&q=85"
          alt="Hero"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 carbon-bg opacity-30" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 w-full">
          <div className="max-w-3xl fade-in">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-16 bg-[var(--gold)]" />
              <span className="label-caps">Vanguard Lux Cars</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1] uppercase mb-6">
              Οδήγησε το<br />
              <span className="gold-gradient">Εξαιρετικό.</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 font-light italic leading-relaxed mb-10 max-w-xl">
              Επιμελημένη συλλογή από εξαιρετικά αυτοκίνητα. Διαθέσιμα για αγορά ή βραχυχρόνια ενοικίαση.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/buy" data-testid="hero-buy-btn" className="btn-primary flex items-center gap-3">
                Δείτε τη Συλλογή <ArrowRight size={16} />
              </Link>
              <Link to="/rent" data-testid="hero-rent-btn" className="btn-secondary">
                Ενοικιάστε
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-white/5 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { icon: Shield, title: "Πιστοποιημένη Συλλογή", text: "Κάθε όχημα ελέγχεται και πιστοποιείται από τους τεχνικούς μας." },
            { icon: Award, title: "Υπηρεσίες Πολυτελείας", text: "Προσωπικός σύμβουλος από την αρχική επαφή έως την παράδοση." },
            { icon: Sparkles, title: "Πρότυπα Πολυτελείας", text: "Μόνο οι κορυφαίες μάρκες. Από Range Rover μέχρι Mercedes." },
          ].map((f, i) => (
            <div key={i} className="flex gap-5 items-start">
              <div className="h-12 w-12 border border-[var(--gold)]/30 flex items-center justify-center shrink-0">
                <f.icon size={20} className="text-[var(--gold)]" />
              </div>
              <div>
                <div className="font-display font-bold tracking-tight mb-1 text-lg">{f.title}</div>
                <div className="text-base text-gray-400 font-light">{f.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex flex-wrap items-end justify-between mb-16 gap-6">
          <div>
            <div className="label-caps mb-4">Επιλεγμένα Μοντέλα</div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight uppercase">
              Νέες Παραλαβές
            </h2>
          </div>
          <Link to="/buy" className="text-sm tracking-widest uppercase text-gray-400 hover:text-[var(--gold)] transition-colors flex items-center gap-2 font-display">
            Δείτε Όλα <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featured.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
        </div>
      </section>

      {/* NOTIFY ME */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="bg-[#0e0e0e]/80 backdrop-blur-sm border border-[var(--gold)]/20 p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-[var(--gold)]/10 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <Bell size={16} className="text-[var(--gold)]" />
                <span className="label-caps">Μείνετε Ενημερωμένοι</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight uppercase leading-tight mb-4">
                Ενημερωθείτε για<br />
                <span className="gold-gradient">Νέες Παραλαβές.</span>
              </h2>
              <p className="text-base text-gray-400 font-light max-w-md leading-relaxed">
                Τα πιο αποκλειστικά μας οχήματα συχνά πωλούνται πριν φτάσουν στον δημόσιο κατάλογο. Γίνετε μέλος της λίστας και μάθετε πρώτοι.
              </p>
            </div>
            <form onSubmit={subscribe} className="space-y-6">
              <div>
                <label className="label-caps block mb-3">Διεύθυνση Email</label>
                <input
                  data-testid="notify-email"
                  type="email"
                  required
                  placeholder="email@example.com"
                  className="input-line"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <p className="text-[0.65rem] text-gray-500 tracking-widest uppercase max-w-xs leading-relaxed font-display">
                  Καμία ανεπιθύμητη αλληλογραφία. Μόνο νέες αφίξεις.
                </p>
                <button
                  type="submit"
                  data-testid="notify-submit"
                  disabled={subscribing}
                  className="btn-primary"
                >
                  {subscribing ? "Εγγραφή..." : "Ενημερωθείτε"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1588911146469-befbc6e2145a?w=2000&q=85"
          alt="CTA"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <img src={LOGO_URL} alt="Vanguard" className="h-20 w-20 mb-8 object-contain" />
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-light tracking-wide max-w-3xl leading-snug italic">
            Το Επόμενο Κεφάλαιο<br />Ξεκινά με τη <span className="gold-gradient not-italic">Σωστή Διαδρομή.</span>
          </h2>
          <div className="shimmer-divider my-10" />
          <div className="flex flex-wrap gap-4">
            <Link to="/buy" data-testid="cta-buy" className="btn-primary">Προς Πώληση</Link>
            <Link to="/rent" data-testid="cta-rent" className="btn-secondary">Προς Ενοικίαση</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
